import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { FileSpreadsheet, FileText, Download, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExportPage() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("students");

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setClasses(response.data);
    } catch (error) {
      console.error("Error fetching classes:", error);
      toast.error("Failed to load classes");
    } finally {
      setLoading(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.parentElement.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // ===== Student Export Functions =====
  const handleExportStudentsExcel = async () => {
    try {
      setExporting(true);
      const params = {};
      if (selectedClass) params.class_id = selectedClass;

      const response = await axios.get(`${API}/export/export-excel`, {
        params,
        responseType: 'blob',
      });

      downloadBlob(response.data, `students_export_${new Date().getTime()}.xlsx`);
      toast.success("Exported to Excel successfully!");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      const msg = error.response?.status === 403
        ? "Access denied. Please log out and log back in."
        : "Failed to export students to Excel. Please try again.";
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const handleExportStudentsPDF = async () => {
    try {
      setExporting(true);
      const params = {};
      if (selectedClass) params.class_id = selectedClass;

      const response = await axios.get(`${API}/export/export-pdf`, {
        params,
        responseType: 'blob',
      });

      downloadBlob(response.data, `students_export_${new Date().getTime()}.pdf`);
      toast.success("Exported to PDF successfully!");
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      const msg = error.response?.status === 403
        ? "Access denied. Please log out and log back in."
        : "Failed to export students to PDF. Please try again.";
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  // ===== Staff Export Functions =====
  const handleExportStaffExcel = async () => {
    try {
      setExporting(true);
      const response = await axios.get(`${API}/export/export-staff-excel`, {
        responseType: 'blob',
      });

      downloadBlob(response.data, `staff_export_${new Date().getTime()}.xlsx`);
      toast.success("Staff exported to Excel successfully!");
    } catch (error) {
      console.error("Error exporting staff to Excel:", error);
      const msg = error.response?.status === 403
        ? "Access denied. Please log out and log back in."
        : "Failed to export staff to Excel. Please try again.";
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const handleExportStaffPDF = async () => {
    try {
      setExporting(true);
      const response = await axios.get(`${API}/export/export-staff-pdf`, {
        responseType: 'blob',
      });

      downloadBlob(response.data, `staff_export_${new Date().getTime()}.pdf`);
      toast.success("Staff exported to PDF successfully!");
    } catch (error) {
      console.error("Error exporting staff to PDF:", error);
      const msg = error.response?.status === 403
        ? "Access denied. Please log out and log back in."
        : "Failed to export staff to PDF. Please try again.";
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const StudentExportTab = () => (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Export Students</CardTitle>
        <p className="text-xs text-gray-500 mt-1">Filter and export student data by class</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label htmlFor="class-select" className="text-xs font-medium">Class</Label>
          <Select value={selectedClass || ""} onValueChange={setSelectedClass}>
            <SelectTrigger id="class-select" className="h-8">
              <SelectValue placeholder="All Classes" />
            </SelectTrigger>
            <SelectContent>
              {classes.map((cls) => (
                <SelectItem key={cls.id} value={cls.id}>
                  {cls.display_name || cls.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button
            onClick={handleExportStudentsExcel}
            disabled={exporting}
            size="sm"
            className="text-xs h-8"
          >
            <FileSpreadsheet className="w-3 h-3 mr-1" />
            Excel
          </Button>

          <Button
            onClick={handleExportStudentsPDF}
            disabled={exporting}
            variant="outline"
            size="sm"
            className="text-xs h-8"
          >
            <FileText className="w-3 h-3 mr-1" />
            PDF
          </Button>
        </div>

        {exporting && (
          <p className="text-xs text-center text-gray-500">
            Preparing export...
          </p>
        )}
      </CardContent>
    </Card>
  );

  const StaffExportTab = () => (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="w-4 h-4" />
          Export Staff
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1">Export all staff/teacher details</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
          Exports complete staff registry with contact info, assignments, and classes.
        </p>

        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={handleExportStaffExcel}
            disabled={exporting}
            size="sm"
            className="text-xs h-8"
          >
            <FileSpreadsheet className="w-3 h-3 mr-1" />
            Excel
          </Button>

          <Button
            onClick={handleExportStaffPDF}
            disabled={exporting}
            variant="outline"
            size="sm"
            className="text-xs h-8"
          >
            <FileText className="w-3 h-3 mr-1" />
            PDF
          </Button>
        </div>

        {exporting && (
          <p className="text-xs text-center text-gray-500">
            Preparing export...
          </p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="p-4 md:p-6 max-w-2xl">
      <div className="mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Download className="w-6 h-6" />
          Export Data
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Export student and staff information
        </p>
      </div>

      {/* Compact Tab Navigation */}
      <div className="flex gap-2 mb-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("students")}
          className={`px-3 py-2 text-xs font-medium transition-colors ${
            activeTab === "students"
              ? "text-pink-600 border-b-2 border-pink-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Students
        </button>
        <button
          onClick={() => setActiveTab("staff")}
          className={`px-3 py-2 text-xs font-medium transition-colors ${
            activeTab === "staff"
              ? "text-pink-600 border-b-2 border-pink-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Staff
        </button>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === "students" && <StudentExportTab />}
        {activeTab === "staff" && <StaffExportTab />}
      </div>

      {/* Info Card */}
      <Card className="mt-4 bg-blue-50 border-blue-200">
        <CardHeader className="py-3">
          <CardTitle className="text-sm text-blue-900">What's Included</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1 text-xs text-blue-900">
            {activeTab === "students" ? (
              <>
                <li>✓ Student ID and Name</li>
                <li>✓ Gender and Date of Birth</li>
                <li>✓ Class Assignment</li>
                <li>✓ Parent Information</li>
                <li>✓ Contact & Address</li>
                <li>✓ Fee Status</li>
              </>
            ) : (
              <>
                <li>✓ Full Name and Email</li>
                <li>✓ National ID</li>
                <li>✓ Phone Number</li>
                <li>✓ Physical Address</li>
                <li>✓ Class Assignment</li>
                <li>✓ Assigned Subjects</li>
              </>
            )}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
